import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Plus, Calendar, Clock, MapPin, User, CheckCircle, AlertCircle, Eye, Filter, Search } from 'lucide-react';
import api from '../../api/apiClient';

interface Inspection {
  id: string;
  propertyId: number;
  propertyAddress: string;
  leaseId: number;
  tenantName: string;
  tenantEmail: string;
  scheduledAt: string;
  status: string;
  tenantConfirmed: boolean;
  inspectionType: string;
  notes: string;
  agentName: string;
  reportUrl?: string;
}

export default function InspectionManagement() {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadInspections();
  }, []);

  const loadInspections = async () => {
    setLoading(true);
    try {
      const response = await api.get<Inspection[]>('/inspections');
      setInspections(response || []);
    } catch (err) {
      console.error('Failed to load inspections:', err);
      setInspections([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'SCHEDULED': return 'bg-blue-100 text-blue-800';
      case 'CONFIRMED': return 'bg-purple-100 text-purple-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'ROUTINE': return 'Routine';
      case 'ENTRY': return 'Entry';
      case 'EXIT': return 'Exit';
      case 'MAINTENANCE': return 'Maintenance';
      default: return type || 'General';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isUpcoming = (dateString: string) => {
    return new Date(dateString) > new Date();
  };

  const filteredInspections = inspections.filter(inspection => {
    const matchesFilter = filter === 'all' ||
      (filter === 'upcoming' && isUpcoming(inspection.scheduledAt) && inspection.status !== 'COMPLETED') ||
      (filter === 'completed' && inspection.status === 'COMPLETED') ||
      (filter === 'pending' && !inspection.tenantConfirmed && inspection.status === 'SCHEDULED');

    const matchesSearch = !searchTerm ||
      inspection.propertyAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.tenantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.inspectionType?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: inspections.length,
    upcoming: inspections.filter(i => isUpcoming(i.scheduledAt) && i.status !== 'COMPLETED').length,
    pendingConfirmation: inspections.filter(i => !i.tenantConfirmed && i.status === 'SCHEDULED').length,
    completed: inspections.filter(i => i.status === 'COMPLETED').length,
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Inspections</h1>
            <p className="text-gray-600">Schedule and manage property inspections</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/agent/inspections/create')}
          className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
        >
          <Plus className="w-5 h-5" />
          Schedule Inspection
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${filter === 'all' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <ClipboardCheck className="w-8 h-8 text-gray-200" />
          </div>
        </button>

        <button
          onClick={() => setFilter('upcoming')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${filter === 'upcoming' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Upcoming</p>
              <p className="text-2xl font-bold text-blue-600">{stats.upcoming}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-100" />
          </div>
        </button>

        <button
          onClick={() => setFilter('pending')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${filter === 'pending' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Confirmation</p>
              <p className="text-2xl font-bold text-orange-600">{stats.pendingConfirmation}</p>
            </div>
            <Clock className="w-8 h-8 text-orange-100" />
          </div>
        </button>

        <button
          onClick={() => setFilter('completed')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${filter === 'completed' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-100" />
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by address, tenant, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            >
              <option value="all">All Inspections</option>
              <option value="upcoming">Upcoming</option>
              <option value="pending">Pending Confirmation</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Inspections List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {filter === 'all' ? 'All Inspections' : filter === 'upcoming' ? 'Upcoming Inspections' : filter === 'pending' ? 'Pending Confirmation' : 'Completed Inspections'}
          </h2>
        </div>

        {filteredInspections.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No inspections found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ? 'Try adjusting your search terms.' : 'Schedule your first inspection to get started.'}
            </p>
            <button
              onClick={() => navigate('/agent/inspections/create')}
              className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
            >
              Schedule Inspection
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredInspections.map(inspection => (
              <div
                key={inspection.id}
                className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/agent/inspections/${inspection.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(inspection.status)}`}>
                        {inspection.status?.replace(/_/g, ' ')}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        {getTypeLabel(inspection.inspectionType)}
                      </span>
                      {inspection.tenantConfirmed ? (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle className="w-3 h-3" /> Tenant Confirmed
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-orange-600">
                          <AlertCircle className="w-3 h-3" /> Awaiting Confirmation
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{inspection.propertyAddress}</span>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(inspection.scheduledAt)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {inspection.tenantName || 'No tenant'}
                      </span>
                    </div>

                    {inspection.notes && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-1">{inspection.notes}</p>
                    )}
                  </div>

                  <button
                    className="p-2 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/agent/inspections/${inspection.id}`);
                    }}
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

